import { useNavigate } from "react-router-dom";
import { products, services, testimonials } from "@/mocks/products";
import { ProductCard, SectionTitle, CtaButton } from "@/components/base/ui";

export default function Home() {
  const navigate = useNavigate();
  const featuredProducts = products.filter((p) => p.featured || p.id <= 4);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] lg:h-[800px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Luxury%20white%20marble%20quarry%20interior%20with%20dramatic%20lighting%2C%20large%20marble%20slabs%20stacked%20in%20warehouse%2C%20natural%20stone%20material%2C%20industrial%20elegance%2C%20warm%20ambient%20light%2C%20professional%20architectural%20photography%2C%20high%20contrast%2C%20dark%20and%20moody%20atmosphere%20with%20golden%20light%20accents%2C%20premium%20stone%20supplier%20aesthetic&width=1600&height=900&seq=8&orientation=landscape"
            alt="Premium marble warehouse"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/60 to-stone-900/40" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm text-white/90 text-xs font-sans uppercase tracking-[0.2em] rounded-full mb-6 border border-white/20">
              Stone of Distinction
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Where Nature
              <br />
              Meets Architecture
            </h1>
            <p className="text-white/80 text-base md:text-lg font-sans max-w-xl leading-relaxed mb-8">
              Lithos is your trusted partner for premium marble, granite, quartzite, and
              travertine. Supplying architects and construction professionals with
              exceptional natural stone.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <CtaButton
                onClick={() => navigate("/products")}
                variant="primary"
              >
                View Catalog
                <i className="ri-arrow-right-line"></i>
              </CtaButton>
              <CtaButton
                onClick={() => navigate("/contact")}
                variant="outline"
              >
                <span className="text-white border-white/50 hover:border-white/80 hover:bg-white/10">
                  Contact Us
                </span>
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-4 block">
                About Us
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 leading-tight mb-6">
                Bridging Natural Stone with European Architecture
              </h2>
              <p className="text-stone-500 text-base md:text-lg font-sans leading-relaxed mb-6">
                We are a French company specializing in the import and
                distribution of premium natural stone. Our
                deep-rooted connections with partner quarries ensure direct
                access to the finest marble and other natural stones.
              </p>
              <p className="text-stone-500 text-base md:text-lg font-sans leading-relaxed mb-8">
                From container logistics to wholesale supply, we deliver
                end-to-end stone solutions for architects, builders, and
                distributors across France and Europe.
              </p>
              <CtaButton
                onClick={() => navigate("/about")}
                variant="secondary"
              >
                Our Story
                <i className="ri-arrow-right-line"></i>
              </CtaButton>
            </div>
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=Marble%20quarry%20landscape%20with%20dramatic%20rocky%20terrain%2C%20natural%20stone%20extraction%20site%2C%20warm%20desert%20lighting%2C%20professional%20documentary%20photography%2C%20earthy%20tones%2C%20industrial%20beauty%20in%20nature%2C%20golden%20hour%20lighting%2C%20wide%20panoramic%20view&width=500&height=600&seq=9&orientation=portrait"
                alt="Marble quarry"
                className="w-full h-64 md:h-80 object-cover rounded-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Polished%20marble%20slabs%20stacked%20in%20modern%20warehouse%20interior%2C%20luxury%20stone%20showroom%2C%20professional%20architectural%20photography%2C%20clean%20minimal%20space%2C%20warm%20ambient%20lighting%2C%20beige%20and%20white%20tones%2C%20high-end%20material%20storage&width=500&height=600&seq=10&orientation=portrait"
                alt="Marble warehouse"
                className="w-full h-64 md:h-80 object-cover rounded-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="Our Collection"
            title="Premium Natural Stone"
            subtitle="Discover our curated selection of marble, granite, quartzite, and travertine — each slab hand-selected for exceptional quality."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} {...product} />
            ))}
          </div>
          <div className="text-center mt-10 md:mt-12">
            <CtaButton
              onClick={() => navigate("/products")}
              variant="outline"
            >
              View All Products
              <i className="ri-arrow-right-line"></i>
            </CtaButton>
          </div>
        </div>
      </section>

      {/* Why Our Marble Section */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="Why Choose Us"
            title="Why Choose Our Marble?"
            subtitle="We source from world-class stone regions, offering unique advantages for European buyers."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                icon: "ri-money-euro-circle-line",
                title: "Competitive Price",
                text: "Direct quarry access means better pricing than traditional Italian or Greek alternatives, without sacrificing quality.",
              },
              {
                icon: "ri-award-line",
                title: "Premium Quality",
                text: "Our marble is renowned for its exceptional purity, density, and beautiful veining — equal to the world's finest stones.",
              },
              {
                icon: "ri-truck-line",
                title: "Fast Shipping",
                text: "Strategic location just across the Mediterranean. Containers reach French ports within days, not weeks.",
              },
              {
                icon: "ri-global-line",
                title: "Emerging Market",
                text: "Be among the first to offer exclusive stone from our partner quarries. Stand out from competitors using common materials.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg p-6 md:p-8 border border-stone-200 hover:border-stone-300 transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-stone-50 rounded-lg mb-5">
                  <i className={`${item.icon} text-gold-500 text-xl`}></i>
                </div>
                <h3 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-stone-500 text-sm font-sans leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="What We Offer"
            title="Our Services"
            subtitle="End-to-end stone solutions for B2B clients — from quarry to construction site."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="group bg-stone-50 rounded-lg p-6 md:p-8 border border-stone-100 hover:border-stone-200 transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg mb-5 border border-stone-200 group-hover:border-gold-300 transition-colors">
                  <i className={`${service.icon} text-stone-700 text-xl`}></i>
                </div>
                <h3 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-stone-500 text-sm font-sans leading-relaxed mb-5">
                  {service.description}
                </p>
                <button
                  onClick={() => navigate("/services")}
                  className="text-gold-500 text-sm font-sans font-medium hover:text-gold-600 transition-colors cursor-pointer"
                >
                  Learn more
                  <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="Testimonials"
            title="Trusted by Industry Professionals"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="bg-white rounded-lg p-6 md:p-8 border border-stone-200"
              >
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className="ri-star-fill text-gold-500 text-sm"
                    ></i>
                  ))}
                </div>
                <p className="text-stone-600 text-sm font-sans leading-relaxed mb-6">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                    <span className="text-stone-600 font-sans text-sm font-medium">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-stone-900 text-sm font-sans font-medium">
                      {t.name}
                    </p>
                    <p className="text-stone-400 text-xs font-sans">
                      {t.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 lg:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Shipping%20container%20port%20at%20sunset%20with%20Mediterranean%20sea%2C%20industrial%20cargo%20logistics%2C%20dramatic%20golden%20sky%2C%20warm%20orange%20and%20amber%20tones%2C%20professional%20photography%2C%20container%20yard%20with%20cranes%2C%20import%20export%20business%20atmosphere%2C%20cinematic%20wide%20angle%20view&width=1600&height=600&seq=11&orientation=landscape"
            alt="Container shipping port"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/70" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Ready to Source Premium Stone?
            </h2>
            <p className="text-white/80 text-base md:text-lg font-sans max-w-2xl mx-auto leading-relaxed mb-8">
              Get a personalized quotation for your project. We offer
              competitive pricing, reliable logistics, and consistent quality for
              B2B orders of any size.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <CtaButton
                onClick={() => navigate("/quote")}
                variant="primary"
              >
                Request a Quotation
                <i className="ri-arrow-right-line"></i>
              </CtaButton>
              <CtaButton
                onClick={() => navigate("/contact")}
                variant="outline"
              >
                <span className="text-white border-white/50 hover:border-white/80 hover:bg-white/10">
                  Contact Us
                </span>
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}